import React from "react";
import { z } from "zod";
import { Box, Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useProgressView,
  useUpdateNotificationSettings,
} from "@quest_editor/mocks";
import {
  type DayOfWeekValue,
  PROGRESS_NOTIFICATION_MAX_COUNT,
} from "@quest_editor/constants";
import QuestListSection from "./QuestListSection";
import NotificationSettingsSection from "./NotificationSettingsSection";

type NotificationSettingsFormData = {
  rows: { email: string; days: DayOfWeekValue[] }[];
};

const notificationSettingsSchema = z.object({
  rows: z
    .array(z.object({ email: z.string(), days: z.array(z.string()) }))
    .length(PROGRESS_NOTIFICATION_MAX_COUNT),
});

const createDefaultValues = (): NotificationSettingsFormData => ({
  rows: Array(PROGRESS_NOTIFICATION_MAX_COUNT)
    .fill(null)
    .map(() => ({ email: "", days: [] })),
});

const PromotionProgress = () => {
  const { t } = useTranslation("questEditor");
  const { hash } = useParams<{ hash: string }>();

  const { data } = useProgressView(hash);
  const promotion = data?.promotion;
  const quests = data?.quests ?? [];

  const { updateNotificationSettings } = useUpdateNotificationSettings();

  const formMethods = useForm<NotificationSettingsFormData>({
    resolver: zodResolver(notificationSettingsSchema),
    defaultValues: createDefaultValues(),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  const initializedHashRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (!data || initializedHashRef.current === hash) return;

    initializedHashRef.current = hash;
    const notifications = data.notifications ?? [];

    const rows = notifications.map((n) => ({
      email: n.email,
      days: [...n.days],
    }));

    while (rows.length < PROGRESS_NOTIFICATION_MAX_COUNT) {
      rows.push({ email: "", days: [] });
    }

    reset({
      rows: rows.slice(0, PROGRESS_NOTIFICATION_MAX_COUNT),
    });

    const errors = notifications
      .map((n, i) =>
        n.hasError && n.errorMessage
          ? { index: i, message: n.errorMessage }
          : null,
      )
      .filter((e): e is { index: number; message: string } => e !== null);
    setServerErrors(errors);
  }, [data, hash, reset]);

  const [serverErrors, setServerErrors] = React.useState<
    { index: number; message: string }[]
  >([]);

  const onSubmit = async (formData: NotificationSettingsFormData) => {
    if (!promotion) return;

    const validSettings = formData.rows
      .filter((row) => row.email.trim() !== "")
      .map((row) => ({
        email: row.email,
        days: row.days,
      }));

    await updateNotificationSettings({
      promotionId: promotion.id,
      settings: validSettings,
    });
  };

  if (!promotion) {
    return (
      <Box
        sx={{
          height: "100vh",
          bgcolor: "grey.100",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t("promotion_progress.not_found")}
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        direction="column"
        spacing={3}
        sx={{ p: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          {promotion.name}
        </Typography>

        <QuestListSection quests={quests} />

        <NotificationSettingsSection serverErrors={serverErrors} />

        <Box display="flex" justifyContent="center">
          <ActionButton
            type="submit"
            disabled={isSubmitting}
          >
            {t("promotion_progress.button.update_settings")}
          </ActionButton>
        </Box>
      </Stack>
    </FormProvider>
  );
};

export default PromotionProgress;
